import { createForm, updateForm, updateFormImage } from "js/Api/forms";
import Loader from "js/Component/Loader";
import ModalWrapper from "js/Component/ModalWrapper";
import { useState, useEffect, memo } from "react";
import toast from "react-hot-toast";

const FormModal = (props) => {
    const {
        visible,
        onClose,
        onSaved,
        org,
        editableItem,
        isTemplate = false,
    } = props;

    // Form state
    const [formData, setFormData] = useState({
        name: "",
        description: "",
        formType: "general",
        displayType: "normal",
    });

    const [errors, setErrors] = useState({});
    const [apiError, setApiError] = useState("");
    const [selectedImage, setSelectedImage] = useState(null);
    const [imageURL, setImageURL] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);
    const [hasTriedSubmit, setHasTriedSubmit] = useState(false);
    const [openAutomatically, setOpenAutomatically] = useState(false); // New state for checkbox
    const [hasFormType, sethasFormType] = useState(true);

    const isEditMode = !!editableItem?.id;

    // Load data when editing
    useEffect(() => {
        if (editableItem?.id) {

            if (editableItem.formType === null || editableItem.formType === "")
                sethasFormType(false);

            setFormData({
                name: editableItem.formName || "",
                description: editableItem.description || "",
                formType: editableItem.formType || "general",
                displayType: editableItem.displayType || "normal",
            });
            setImageURL(editableItem.imageURL || "");
            // Set checkbox state based on displayType
            setOpenAutomatically(editableItem.displayType === "fullscreen-dialog");
        } else {
            // Reset for new item
            setFormData({
                name: "",
                description: "",
                formType: "general",
                displayType: "normal",
            });
            setImageURL("");
            setOpenAutomatically(false);
        }

        setHasTriedSubmit(false);
        setErrors({});
    }, [editableItem]);

    // Validation
    const validateField = (fieldName, value) => {
        let error = "";
        if (fieldName === "name" && value.trim().length < 2) {
            error = "Name must be at least 2 characters.";
        }
        if (fieldName === "description" && value.trim().length < 2) {
            error = "Description must be at least 2 characters.";
        }
        return error;
    };

    useEffect(() => {
        const nameError = validateField("name", formData.name);
        const descError = validateField("description", formData.description);

        if (hasTriedSubmit) {
            setErrors({
                name: nameError,
                description: descError,
            });
        }

        const noErrors = !nameError && !descError;
        const hasMinimumLength = formData.name.trim().length >= 2 && formData.description.trim().length >= 2;
        setIsFormValid(noErrors && hasMinimumLength);
    }, [formData.name, formData.description, hasTriedSubmit]);

    // Event handlers
    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleOpenAutomaticallyChange = (e) => {
        const checked = e.target.checked;
        setOpenAutomatically(checked);
        // Update displayType based on checkbox state
        setFormData(prev => ({
            ...prev,
            displayType: checked ? "fullscreen-dialog" : "normal"
        }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const allowedTypes = ["image/jpeg", "image/png"];
            const maxSizeInBytes = 15 * 1024 * 1024; // 15 MB

            if (!allowedTypes.includes(file.type)) {
                setErrors(prev => ({ ...prev, image: "Only PNG and JPG files are allowed." }));
                setSelectedImage(null);
                setImageURL("");
                return;
            }

            if (file.size > maxSizeInBytes) {
                setErrors(prev => ({ ...prev, image: "Image size must be less than 15 MB." }));
                setSelectedImage(null);
                setImageURL("");
                return;
            }

            setSelectedImage(file);
            setImageURL(URL.createObjectURL(file));
            setErrors(prev => ({ ...prev, image: "" }));
        }
    };

    const handleRemoveImage = () => {
        setSelectedImage(null);
        setImageURL("");
    };

    const handleClose = () => {
        setFormData({
            name: "",
            description: "",
            formType: "general",
            displayType: "normal",
        });
        setErrors({});
        setApiError("");
        setImageURL("");
        setSelectedImage(null);
        setHasTriedSubmit(false);
        setOpenAutomatically(false); // Reset checkbox state
        onClose();
    };

    // API calls
    const createItem = async (payload) => {
        setIsLoading(true);
        try {
            const response = await createForm(payload);
            if (response?.success) {
                if (selectedImage) {
                    await updateFormImage(response?.data?.id, selectedImage, false);
                }
                toast.success(`${isTemplate ? "Template" : "Form"} created successfully`);
                onSaved(response?.data, true);
                handleClose();
            } else {
                setApiError(response?.error || `Failed to create ${isTemplate ? "template" : "form"}`);
            }
        } catch (error) {
            setApiError(`Failed to create ${isTemplate ? "template" : "form"}`);
            console.error("Error creating item:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const updateItem = async (payload) => {
        setIsLoading(true);
        try {
            const response = await updateForm(payload);
            if (response?.success) {
                if (selectedImage) {
                    await updateFormImage(response?.data?.id, selectedImage, false);
                }
                toast.success(`${isTemplate ? "Template" : "Form"} updated successfully`);
                onSaved(response?.data, false);

                handleClose();
            } else {
                setApiError(response?.error || `Failed to update ${isTemplate ? "template" : "form"}`);
            }
        } catch (error) {
            setApiError(`Failed to update ${isTemplate ? "template" : "form"}`);
            console.error("Error updating item:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        setHasTriedSubmit(true);

        if (!isFormValid) return;

        const storedUserInfo = localStorage.getItem("userInfo") ?? "{}";
        const parsedUserInfo = JSON.parse(storedUserInfo);

        let payload;

        if (isEditMode) {

            payload = {
                ...editableItem,

                formName: formData.name.trim(),
                description: formData.description.trim(),
                displayType: formData.displayType,

                userId: parsedUserInfo?.userId,
            };
        } else {

            payload = {
                organizationId: org?.id,
                formName: formData.name.trim(),
                description: formData.description.trim(),
                formType: formData.formType,
                displayType: formData.displayType,
                userId: parsedUserInfo?.userId,
                ...(isTemplate && { templateType: "recurring" }),
            };
        }

        if (isEditMode) {
            await updateItem(payload);
        } else {
            await createItem(payload);
        }
    };

    if (!visible) return null;

    return (
        <ModalWrapper>
            <div className="modal-header">
                <h1 className="modal-title">
                    {isEditMode ? "Edit" : "Add"} {isTemplate ? "Template" : "Form"}
                </h1>
                <button className="close-button" onClick={handleClose}>
                    &times;
                </button>
            </div>

            <div className="modal-body" style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
                <div className="contact-form">
                    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>

                        {/* Name Field */}
                        <div style={{ display: "flex", flexDirection: "column", width: "400px" }}>
                            <label htmlFor="name">Name</label>
                            <input
                                className="form-control"
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={(e) => handleInputChange("name", e.target.value)}
                            />
                            {errors.name && (
                                <span style={{ color: "red", fontSize: "14px" }}>
                                    {errors.name}
                                </span>
                            )}
                        </div>

                        {/* Description Field */}
                        <div style={{ display: "flex", flexDirection: "column", width: "400px" }}>
                            <label htmlFor="description">Description</label>
                            <textarea
                                className="form-control"
                                id="description"
                                name="description"
                                rows="4"
                                value={formData.description}
                                onChange={(e) => handleInputChange("description", e.target.value)}
                            />
                            {errors.description && (
                                <span style={{ color: "red", fontSize: "14px" }}>
                                    {errors.description}
                                </span>
                            )}
                        </div>

                        {/* Image Upload */}
                        <div>
                            <label>Image (Optional)</label>
                            <br />
                            {imageURL ? (
                                <div style={{ marginTop: "10px" }}>
                                    <img
                                        src={imageURL}
                                        alt="Preview"
                                        style={{
                                            width: "200px",
                                            height: "200px",
                                            borderRadius: "8px",
                                            objectFit: "contain",
                                        }}
                                    />
                                    <br />
                                    <button
                                        type="button"
                                        onClick={handleRemoveImage}
                                        style={{
                                            marginTop: "10px",
                                            padding: "8px 16px",
                                            borderRadius: "4px",
                                            border: "1px solid #ddd",
                                            backgroundColor: "#f8f9fa",
                                            cursor: "pointer"
                                        }}
                                    >
                                        Remove Image
                                    </button>
                                </div>
                            ) : (
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                    style={{ marginTop: "8px" }}
                                />
                            )}
                            {errors.image && (
                                <span style={{ color: "red", fontSize: "14px" }}>
                                    {errors.image}
                                </span>
                            )}
                        </div>

                        {/* Form Type Section */}
                        <div style={{ display: "flex", flexDirection: "column", width: "400px", marginBottom: "12px" }}>
                            <label style={{ fontWeight: "bold", marginBottom: "12px" }}>
                                Form type
                            </label>
                            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                <label style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    cursor: isEditMode && hasFormType ? "not-allowed" : "pointer",
                                    opacity: isEditMode && hasFormType ? 0.6 : 1
                                }}>
                                    <input
                                        type="radio"
                                        name="formType"
                                        value="general"
                                        checked={formData.formType === "general"}
                                        onChange={(e) => handleInputChange("formType", e.target.value)}
                                        disabled={isEditMode && hasFormType}
                                        style={{ width: "16px", height: "16px" }}
                                    />
                                    General
                                </label>
                                <label style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    cursor: isEditMode && hasFormType ? "not-allowed" : "pointer",
                                    opacity: isEditMode && hasFormType ? 0.6 : 1
                                }}>
                                    <input
                                        type="radio"
                                        name="formType"
                                        value="checklist"
                                        checked={formData.formType === "checklist"}
                                        onChange={(e) => handleInputChange("formType", e.target.value)}
                                        disabled={isEditMode && hasFormType} 
                                        style={{ width: "16px", height: "16px" }}
                                    />
                                    Checklist
                                </label>
                                <label style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    cursor: isEditMode && hasFormType ? "not-allowed" : "pointer",
                                    opacity: isEditMode && hasFormType ? 0.6 : 1
                                }}>
                                    <input
                                        type="radio"
                                        name="formType"
                                        value="single-question"
                                        checked={formData.formType === "single-question"}
                                        onChange={(e) => handleInputChange("formType", e.target.value)}
                                        disabled={isEditMode && hasFormType}
                                        style={{ width: "16px", height: "16px" }}
                                    />
                                    Single-question
                                </label>
                            </div>
                        </div>

                        {/* Display Type Checkbox Section */}
                        <div style={{ display: "flex", flexDirection: "column", width: "400px" }}>
                            <label style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                cursor: "pointer",
                                fontWeight: "normal"
                            }}>
                                <input
                                    type="checkbox"
                                    checked={openAutomatically}
                                    onChange={handleOpenAutomaticallyChange}
                                    style={{ width: "16px", height: "16px" }}
                                />
                                Open automatically at start time
                            </label>
                        </div>

                    </div>
                </div>

                {/* Modal Footer */}
                <div className="modal-footer" style={{ marginTop: "24px", display: "flex", gap: "12px", justifyContent: "flex-end" }}>
                    <button
                        type="button"
                        className="btn btn-light"
                        onClick={handleClose}
                        disabled={isLoading}
                        style={{
                            padding: "8px 16px",
                            cursor: isLoading ? "not-allowed" : "pointer"
                        }}
                    >
                        Cancel
                    </button>

                    {isLoading ? (
                        <Loader />
                    ) : (
                        <button
                            type="button"
                            className="btn btn-dark"
                            onClick={handleSubmit}
                            disabled={false}
                            style={{
                                padding: "8px 16px",
                                cursor: "pointer",
                            }}
                        >
                            Save
                        </button>
                    )}
                </div>

                {/* API Error Display */}
                {apiError && (
                    <div className="alert alert-danger" role="alert" style={{ marginTop: "16px" }}>
                        {apiError}
                    </div>
                )}
            </div>
        </ModalWrapper>
    );
};

export default memo(FormModal);